// DiceRollAnimator: a reusable, engine-agnostic 2D dice roll animation driver.
//
// HARD trust boundary: this class NEVER generates or accepts authoritative
// randomness. It is GIVEN the final server dice values + match flags and only
// produces VISUAL frames. Intermediate "tumbling" faces are a deterministic
// local sequence used purely for display; the dice ALWAYS settle on the exact
// server values, and those final values are written last. Animation failure,
// cancellation, orientation/visibility changes never call back to the server.
//
// It is decoupled from PixiJS via the `onFrame` render hook and an injectable
// clock/scheduler, so the entire timeline is unit-testable in jsdom. The scene
// adapts each emitted DieFrame onto a Pixi container.

export type HighlightState = 'none' | 'match' | 'dim';

/** A single visual frame for one die. All transforms are RELATIVE to rest. */
export interface DieFrame {
  /** Visual face 1..6. During tumble this cycles; it ALWAYS ends on the server value. */
  face: number;
  /** Rotation in radians. */
  rotation: number;
  scaleX: number;
  scaleY: number;
  /** Pixel offset from the die's resting position. */
  offsetX: number;
  offsetY: number;
  skewX: number;
  /** Highlight state applied after the authoritative reveal. */
  highlight: HighlightState;
  /** Glow intensity 0..1 (used for match emphasis + anticipation pulse). */
  glow: number;
  /** Motion blur strength 0..1 (peaks during tumble; 0 when reduced-motion). */
  blur: number;
  /** Shadow scale 0..1 (compresses on impact). */
  shadow: number;
}

export type FrameSink = (dieIndex: number, frame: DieFrame) => void;

/** Deterministic visual-only face sequencer (NOT outcome data). */
export type FaceSequencer = (dieIndex: number, tick: number) => number;

export interface DiceAnimatorConfig {
  dieCount?: number;
  onFrame: FrameSink;
  /** Injectable monotonic clock (ms). Defaults to performance.now. */
  now?: () => number;
  /** Injectable rAF. Defaults to requestAnimationFrame. */
  raf?: (cb: (t: number) => void) => number;
  /** Injectable cancel. Defaults to cancelAnimationFrame. */
  cancelRaf?: (handle: number) => void;
  /** Visual-only intermediate face sequence. Deterministic by default. */
  faceSequencer?: FaceSequencer;
}

export interface RollOptions {
  reducedMotion?: boolean;
  /** Multiplies all durations (e.g. 0.5 for "fast"). Clamped to [0.1, 3]. */
  speedScale?: number;
  onComplete?: () => void;
}

export interface RollResult {
  /** True if the roll finished naturally; false if cancelled/disposed. */
  completed: boolean;
}

const TWO_PI = Math.PI * 2;

// Base timing (ms) at speedScale 1. Per-die stagger makes the three dice stop
// at slightly different times so they read as independent objects.
const T_ANTICIPATION = 220;
const T_LAUNCH = 200;
const T_TUMBLE = 620;
const T_BOUNCE = 360;
const T_STAGGER = 130;
const T_REVEAL_HOLD = 260;
const FACE_CYCLE_MS = 70;
const REDUCED_FADE_MS = 140;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function easeInQuad(t: number): number {
  return t * t;
}
// A decaying bounce on landing: returns to 0 with one or two diminishing hops.
function bounceSettle(t: number): number {
  // t in [0,1]; returns vertical offset factor (0 at start and end, dips up).
  const decay = Math.exp(-3.2 * t);
  return Math.sin(t * Math.PI * 2.5) * decay;
}

/** Default deterministic face sequence: a per-die LCG. NEVER outcome data. */
function defaultFaceSequencer(dieIndex: number, tick: number): number {
  let s = (dieIndex + 1) * 2654435761 + tick * 40503;
  s = (s ^ (s >>> 13)) >>> 0;
  return (s % 6) + 1;
}

const REST: DieFrame = {
  face: 1,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
  offsetX: 0,
  offsetY: 0,
  skewX: 0,
  highlight: 'none',
  glow: 0,
  blur: 0,
  shadow: 1,
};

export class DiceRollAnimator {
  private readonly dieCount: number;
  private readonly onFrame: FrameSink;
  private readonly now: () => number;
  private readonly raf: (cb: (t: number) => void) => number;
  private readonly cancelRaf: (handle: number) => void;
  private readonly faceSeq: FaceSequencer;

  private rafHandle: number | undefined;
  private active = false;
  private finalValues: number[] = [];
  private matchFlags: boolean[] = [];
  private resolveCurrent: ((r: RollResult) => void) | undefined;
  private onComplete: (() => void) | undefined;

  constructor(cfg: DiceAnimatorConfig) {
    this.dieCount = cfg.dieCount ?? 3;
    this.onFrame = cfg.onFrame;
    this.now = cfg.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.raf =
      cfg.raf ??
      ((cb) => (typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(cb) : (setTimeout(() => cb(this.now()), 16) as unknown as number)));
    this.cancelRaf =
      cfg.cancelRaf ??
      ((h) => {
        if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(h);
        else clearTimeout(h as unknown as ReturnType<typeof setTimeout>);
      });
    this.faceSeq = cfg.faceSequencer ?? defaultFaceSequencer;
  }

  /** True while a roll animation is in flight. */
  isRunning(): boolean {
    return this.active;
  }

  /**
   * Plays the roll animation and settles on the EXACT server values. If a roll
   * is already running it is finished (snapped to its own final values) first,
   * so there is never more than one active loop. Resolves when the new roll's
   * presentation completes (or is cancelled).
   */
  roll(finalValues: readonly number[], matchFlags: readonly boolean[], opts: RollOptions = {}): Promise<RollResult> {
    if (finalValues.length !== this.dieCount) {
      throw new Error(`expected ${this.dieCount} dice, got ${finalValues.length}`);
    }
    for (const v of finalValues) {
      if (!Number.isInteger(v) || v < 1 || v > 6) throw new Error(`invalid die value ${v}`);
    }
    // Never run two loops at once: finish any in-flight roll deterministically.
    if (this.active) this.cancel();

    this.finalValues = [...finalValues];
    this.matchFlags = finalValues.map((_, i) => Boolean(matchFlags[i]));
    this.onComplete = opts.onComplete;
    this.active = true;

    if (opts.reducedMotion) {
      return this.runReduced();
    }
    return this.runFull(opts);
  }

  /**
   * Stops the animation immediately and SNAPS every die to its authoritative
   * final face + highlight so the saved server outcome stays on screen. Resolves
   * the pending promise as NOT completed and does NOT fire onComplete. Idempotent.
   */
  cancel(): void {
    if (!this.active) return;
    this.stopLoop();
    this.snapToFinal();
    this.active = false;
    const resolve = this.resolveCurrent;
    this.resolveCurrent = undefined;
    this.onComplete = undefined;
    resolve?.({ completed: false });
  }

  /** Tears down the animator. Equivalent to cancel(); safe to call repeatedly. */
  dispose(): void {
    this.cancel();
  }

  private stopLoop(): void {
    if (this.rafHandle !== undefined) {
      this.cancelRaf(this.rafHandle);
      this.rafHandle = undefined;
    }
  }

  private runReduced(): Promise<RollResult> {
    return new Promise<RollResult>((resolve) => {
      this.resolveCurrent = resolve;
      const start = this.now();
      const tick = (): void => {
        if (!this.active) return;
        const elapsed = this.now() - start;
        const p = REDUCED_FADE_MS <= 0 ? 1 : Math.min(1, elapsed / REDUCED_FADE_MS);
        // Quick crossfade in: faces are the FINAL values from the very first
        // frame (no misleading cycling), glow ramps to highlight matches.
        for (let i = 0; i < this.dieCount; i++) {
          const match = this.matchFlags[i] ?? false;
          this.onFrame(i, {
            ...REST,
            face: this.finalValues[i] ?? 1,
            highlight: match ? 'match' : 'dim',
            glow: match ? p : 0,
          });
        }
        if (p >= 1) {
          this.finishNaturally();
        } else {
          this.rafHandle = this.raf(tick);
        }
      };
      tick();
    });
  }

  private runFull(opts: RollOptions): Promise<RollResult> {
    const scale = Math.min(3, Math.max(0.1, opts.speedScale ?? 1));
    const perDie = Array.from({ length: this.dieCount }, (_, i) => this.dieSchedule(i, scale));
    const maxEnd = Math.max(...perDie.map((s) => s.end));
    const revealAt = maxEnd;
    const totalEnd = maxEnd + T_REVEAL_HOLD * scale;

    return new Promise<RollResult>((resolve) => {
      this.resolveCurrent = resolve;
      const start = this.now();
      const tick = (): void => {
        if (!this.active) return;
        const elapsed = this.now() - start;
        for (let i = 0; i < this.dieCount; i++) {
          this.onFrame(i, this.computeFrame(i, elapsed, perDie[i]!, revealAt, scale));
        }
        if (elapsed >= totalEnd) {
          this.finishNaturally();
        } else {
          this.rafHandle = this.raf(tick);
        }
      };
      tick();
    });
  }

  private dieSchedule(i: number, scale: number): { end: number; phases: number[] } {
    const stagger = i * T_STAGGER * scale;
    const ant = T_ANTICIPATION * scale;
    const launch = T_LAUNCH * scale;
    const tumble = T_TUMBLE * scale;
    const bounce = T_BOUNCE * scale;
    const tAnt = stagger + ant;
    const tLaunch = tAnt + launch;
    const tTumble = tLaunch + tumble;
    const tBounce = tTumble + bounce;
    return { end: tBounce, phases: [stagger, tAnt, tLaunch, tTumble, tBounce] };
  }

  private computeFrame(
    i: number,
    elapsed: number,
    sched: { end: number; phases: number[] },
    revealAt: number,
    scale: number,
  ): DieFrame {
    const [startAt, antEnd, launchEnd, tumbleEnd, bounceEnd] = sched.phases as [
      number,
      number,
      number,
      number,
      number,
    ];
    const final = this.finalValues[i] ?? 1;
    const match = this.matchFlags[i] ?? false;

    // Settled: after this die's bounce ends, hold on the AUTHORITATIVE face.
    if (elapsed >= bounceEnd) {
      const revealed = elapsed >= revealAt;
      const glowT = revealed ? Math.min(1, (elapsed - revealAt) / (T_REVEAL_HOLD * scale || 1)) : 0;
      return {
        ...REST,
        face: final,
        highlight: revealed ? (match ? 'match' : 'dim') : 'none',
        glow: revealed && match ? glowT : 0,
      };
    }

    // Pre-start (waiting for stagger): rest.
    if (elapsed < startAt) {
      return { ...REST, face: final };
    }

    // Anticipation: slight pull-back squash + glow pulse.
    if (elapsed < antEnd) {
      const t = (elapsed - startAt) / Math.max(1, antEnd - startAt);
      const squash = 1 - 0.12 * Math.sin(t * Math.PI);
      return {
        ...REST,
        face: final,
        scaleX: 1 + 0.06 * Math.sin(t * Math.PI),
        scaleY: squash,
        offsetY: 6 * Math.sin(t * Math.PI),
        glow: 0.4 * Math.sin(t * Math.PI),
      };
    }

    // Launch: move up/out, rotation begins, slight scale-up.
    if (elapsed < launchEnd) {
      const t = (elapsed - antEnd) / Math.max(1, launchEnd - antEnd);
      return {
        ...REST,
        face: this.cycledFace(i, elapsed, scale),
        rotation: easeInQuad(t) * TWO_PI * (i % 2 === 0 ? 1 : -1),
        scaleX: 1 + 0.08 * t,
        scaleY: 1 + 0.12 * t,
        offsetY: -46 * easeOutCubic(t),
        offsetX: (i - (this.dieCount - 1) / 2) * 6 * t,
        blur: 0.4 * t,
        shadow: 1 - 0.25 * t,
      };
    }

    // Tumble: fast multi-speed rotation, cycling faces, subtle horizontal drift.
    if (elapsed < tumbleEnd) {
      const t = (elapsed - launchEnd) / Math.max(1, tumbleEnd - launchEnd);
      const spin = (2.6 + i * 0.7) * TWO_PI;
      return {
        ...REST,
        face: this.cycledFace(i, elapsed, scale),
        rotation: TWO_PI + spin * t * (i % 2 === 0 ? 1 : -1),
        scaleX: 1.04,
        scaleY: 1.04,
        offsetY: -46 + 40 * easeInQuad(t),
        offsetX: Math.sin((t + i) * Math.PI * 2) * 10,
        skewX: 0.06 * Math.sin((t + i) * Math.PI * 3),
        blur: 0.9 * (1 - t * 0.5),
        shadow: 0.7 + 0.2 * t,
      };
    }

    // Bounce: land with squash-and-stretch + diminishing hops; shadow compresses.
    const t = (elapsed - tumbleEnd) / Math.max(1, bounceEnd - tumbleEnd);
    const hop = bounceSettle(t); // dips above the rest line, settles to 0
    const impact = Math.max(0, 1 - t); // strongest at first contact
    return {
      ...REST,
      // Faces stop cycling early in the bounce and lock to the server value.
      face: t > 0.4 ? final : this.cycledFace(i, elapsed, scale),
      rotation: TWO_PI * (1 - t) * (i % 2 === 0 ? 0.15 : -0.15),
      scaleX: 1 + 0.18 * impact * (hop < 0 ? 1 : -0.5),
      scaleY: 1 - 0.18 * impact * (hop < 0 ? 1 : -0.5),
      offsetY: -36 * Math.max(0, hop),
      blur: 0.2 * impact,
      shadow: 0.7 + 0.3 * (1 - impact),
    };
  }

  private cycledFace(i: number, elapsed: number, scale: number): number {
    const tick = Math.floor(elapsed / Math.max(1, FACE_CYCLE_MS * scale));
    return this.faceSeq(i, tick);
  }

  private snapToFinal(): void {
    for (let i = 0; i < this.dieCount; i++) {
      const match = this.matchFlags[i] ?? false;
      this.onFrame(i, {
        ...REST,
        face: this.finalValues[i] ?? 1,
        highlight: match ? 'match' : 'dim',
        glow: match ? 1 : 0,
      });
    }
  }

  private finishNaturally(): void {
    this.stopLoop();
    this.snapToFinal();
    this.active = false;
    const resolve = this.resolveCurrent;
    const done = this.onComplete;
    this.resolveCurrent = undefined;
    this.onComplete = undefined;
    done?.();
    resolve?.({ completed: true });
  }
}
